import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { createSelector } from '@reduxjs/toolkit';

import type { ISortedAvailableShift, ISortedGroupedSchedule, ISortedShift } from '@/types/schedule';
import type { IWeek, IWeekDay } from '@/types/calendar';
import type { Hue } from '@/types/styles';

import { colors } from '@/styles/colors';
import { useAppDispatch, useAppSelector, useMouseClickOutside, useTimezone, useWeek } from '@/hooks';
import { toggleIsSelectedAvailableShifts } from '@/store/slices/scheduleSlice';
import { BaseShiftModal, SchedulesContainer, ShiftAvailable, ShiftTooltip } from '@/components/agent/my-schedule';
import { PADDING_WEEKDAY } from '@/config/variables';
import { AllShiftItemType } from '@/store/enums';
import { selectScheduleColors, selectSchedulesList } from '@/store/selectors/scheduleSelectors';
import { DlsModalContent } from '@/components/common/calendar/DlsModalContent';
import { selectActiveModalShiftId } from '@/store/selectors/utilsSelectors';
import { classList, getCalendarPosition } from '@/helpers';

interface IProps {
  shift: ISortedShift;
  rowTop: number;
  colSpan: number;
  day: IWeekDay;
  week: IWeek;
}

const allShiftsContainerSelector = createSelector(
  selectSchedulesList,
  selectActiveModalShiftId,
  selectScheduleColors,
  (schedulesList, activeModalShiftId, scheduleColors) => ({
    schedulesList,
    activeModalShiftId,
    scheduleColors,
  }),
);

const AllShiftsContainer = ({ shift, rowTop, colSpan, day, week }: IProps): React.ReactElement => {
  const { selectedDays } = useWeek();
  const [isDlsModalOpen, setIsDlsModalOpen] = useState<boolean>(false);
  const modalRef = useRef<HTMLDivElement>(null);
  useMouseClickOutside(modalRef, () => setIsDlsModalOpen(false));

  const { schedulesList, activeModalShiftId, scheduleColors } = useAppSelector(allShiftsContainerSelector);

  const blockColor = scheduleColors?.[shift.scheduleId];

  const dispatch = useAppDispatch();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isLabelOverlapped, setIsLabelOverlapped] = useState(false);

  const contract = schedulesList.find(schedule => schedule.scheduleId === shift.scheduleId);
  const label = contract?.scheduleNameShort;

  const isSchedule = shift.itemData.itemType === AllShiftItemType.schedule;
  const isActive =
    isSchedule && (shift as ISortedGroupedSchedule).shifts.some(shift => shift.shiftId === activeModalShiftId);

  const scheduleIndex = schedulesList.findIndex(schedule => schedule.scheduleId === contract?.scheduleId) + 1;

  useEffect(() => {
    if (wrapperRef.current && shift.itemData.itemType === AllShiftItemType.shiftAvailable) {
      const title = wrapperRef.current.querySelector('.title');
      const label = title?.querySelector('label') as Element;

      if (!title || !label) {
        return;
      }

      setIsLabelOverlapped(title.clientWidth < label.clientWidth);
    }
  }, [wrapperRef, selectedDays, shift.itemData.itemType]);

  const handleSchedulesContainerClick = useCallback(() => {
    if (shift.isShiftDlsBlocked && week.dst) setIsDlsModalOpen(true);
  }, [shift.isShiftDlsBlocked, week.dst]);

  const dlsModal = useMemo<React.ReactElement | null>(() => {
    if (!shift.isShiftDlsBlocked || !week.dst) {
      return null;
    }
    return isDlsModalOpen ? (
      <BaseShiftModal
        ref={modalRef}
        parentRef={wrapperRef as React.MutableRefObject<HTMLDivElement>}
        content={<DlsModalContent day={day} dst={week.dst} handleClose={() => setIsDlsModalOpen(false)} />}
      />
    ) : (
      <></>
    );
  }, [day, isDlsModalOpen, shift.isShiftDlsBlocked, week.dst]);

  if (!blockColor) return <></>;

  return (
    <ShiftWrapper
      isTooltipAvailable={isSchedule}
      shift={shift}
      scheduleIndex={scheduleIndex}
      day={day}
      rowTop={rowTop}
      colSpan={colSpan}
      isActive={isActive}
      ref={wrapperRef}
      blockColor={blockColor}
    >
      {isSchedule ? (
        <SchedulesContainer
          key={shift.itemData.id}
          shift={shift as ISortedShift<ISortedGroupedSchedule>}
          modal={dlsModal ? () => dlsModal : undefined}
          onClick={handleSchedulesContainerClick}
          day={day}
        />
      ) : (
        <ShiftAvailable
          key={shift.displayBlocks.startBlock}
          block={shift as ISortedShift<ISortedAvailableShift>}
          blockColor={blockColor}
          label={!isLabelOverlapped && label ? label : String(scheduleIndex)}
          onClick={() => {
            if (shift.isShiftDlsBlocked) {
              return setIsDlsModalOpen(true);
            }
            dispatch(toggleIsSelectedAvailableShifts(shift as ISortedAvailableShift));
          }}
          day={day}
          week={week}
          modal={dlsModal ? () => dlsModal : undefined}
        />
      )}
    </ShiftWrapper>
  );
};

export { AllShiftsContainer };

interface IShiftWrapperProps {
  children: React.ReactElement;
  isTooltipAvailable: boolean;
  shift: ISortedShift;
  scheduleIndex: number;
  day: IWeekDay;
  rowTop: number;
  colSpan: number;
  isActive: boolean;
  blockColor: Hue;
}

const ShiftWrapper = React.forwardRef<HTMLDivElement, IShiftWrapperProps>(function ShiftWrapper(props, forwardedRef) {
  const { timezoneName } = useTimezone();

  // adjusted position taking into account DST shifts
  const displayCalendarPosition = getCalendarPosition(
    props.shift.displayBlocks.startBlock,
    props.shift.displayBlocks.endBlock,
    timezoneName,
    props.day,
  );

  return (
    <ShiftTooltip
      canShow={props.isTooltipAvailable}
      shiftData={{
        id: props.shift.itemData.id,
        clientName: props.shift.clientName,
        scheduleName: props.shift.scheduleName,
      }}
      startBlockId={props.shift.originalStartBlock ?? props.shift.itemData.startBlock}
      endBlockId={props.shift.originalEndBlock ?? props.shift.itemData.endBlock}
      scheduleIndex={props.scheduleIndex}
      day={props.day}
    >
      <Wrapper
        minutesFromMidnight={displayCalendarPosition.minutesFromMidnight}
        blockDuration={displayCalendarPosition.blockDuration}
        blockColor={props.blockColor}
        rowTop={props.rowTop}
        colSpan={props.colSpan}
        className={classList(props.isActive && 'is-activated')}
        ref={forwardedRef}
      >
        {props.children}
      </Wrapper>
    </ShiftTooltip>
  );
});

const Wrapper = styled.div<{
  minutesFromMidnight: number;
  blockDuration: number;
  blockColor: Hue;
  rowTop: number;
  colSpan: number;
}>`
  display: flex;
  position: absolute;
  left: 0;
  top: 1px;

  width: calc(${props => props.colSpan} * 100%);
  height: ${props => props.blockDuration - PADDING_WEEKDAY}px;
  padding-left: ${PADDING_WEEKDAY / 2}px;
  padding-right: ${PADDING_WEEKDAY / 2}px;

  transform: translate3d(0, ${props => props.minutesFromMidnight - props.rowTop + 1}px, 0);

  font-size: 10px;
  color: ${props => colors[props.blockColor]['c500']};

  &.is-activated {
    z-index: 1;
  }
`;
